import { type PointerEvent, useEffect, useRef, useState } from "react";
import { Button } from "../primitives";

/**
 * The recipient signs on the glass (DA-10-18, EP10-S18): a full-width canvas
 * drawn with pointer events — finger, stylus or mouse alike — with **Clear**
 * and **Done**. Done hands over a PNG on a white ground, so the signature
 * reads the same on the console's dark theme as on paper. No library: a
 * signature is a polyline.
 *
 * The canvas is drawn at a fixed resolution and scaled by CSS, so a point is
 * mapped from the element's box to the bitmap on every move.
 */
export interface SignaturePadProps {
	/** Names the drawing area for a screen reader. */
	label: string;
	clearLabel: string;
	doneLabel: string;
	/** A line under the pad, e.g. "Turn the phone sideways for more room". */
	hint?: string;
	onDone: (png: Blob) => void;
}

const WIDTH = 900;
const HEIGHT = 300;

/** Paints the canvas white: the ground a signature is exported on. */
function paper(node: HTMLCanvasElement | null) {
	const c = node?.getContext("2d") ?? null;
	if (c !== null) {
		c.fillStyle = "#fff";
		c.fillRect(0, 0, WIDTH, HEIGHT);
	}
}

export function SignaturePad({ label, clearLabel, doneLabel, hint, onDone }: SignaturePadProps) {
	const canvas = useRef<HTMLCanvasElement | null>(null);
	const drawing = useRef(false);
	const [signed, setSigned] = useState(false);

	function context(): CanvasRenderingContext2D | null {
		const c = canvas.current?.getContext("2d") ?? null;
		if (c !== null) {
			c.lineWidth = 4;
			c.lineCap = "round";
			c.lineJoin = "round";
			c.strokeStyle = "#111";
		}
		return c;
	}

	function point(e: PointerEvent<HTMLCanvasElement>) {
		const box = e.currentTarget.getBoundingClientRect();
		return {
			x: ((e.clientX - box.left) / (box.width || WIDTH)) * WIDTH,
			y: ((e.clientY - box.top) / (box.height || HEIGHT)) * HEIGHT,
		};
	}

	function clear() {
		paper(canvas.current);
		setSigned(false);
	}

	// The white ground, once, when the canvas mounts.
	useEffect(() => paper(canvas.current), []);

	function done() {
		canvas.current?.toBlob((png) => {
			if (png !== null) {
				onDone(png);
			}
		}, "image/png");
	}

	return (
		<div className="flex flex-col gap-2">
			<canvas
				ref={canvas}
				width={WIDTH}
				height={HEIGHT}
				role="img"
				aria-label={label}
				className="aspect-[3/1] w-full touch-none rounded-lg border border-border bg-white"
				onPointerDown={(e) => {
					const c = context();
					if (c === null) {
						return;
					}
					e.currentTarget.setPointerCapture?.(e.pointerId);
					drawing.current = true;
					const { x, y } = point(e);
					c.beginPath();
					c.moveTo(x, y);
					// A tap is a dot.
					c.lineTo(x + 0.1, y + 0.1);
					c.stroke();
					setSigned(true);
				}}
				onPointerMove={(e) => {
					const c = drawing.current ? context() : null;
					if (c === null) {
						return;
					}
					const { x, y } = point(e);
					c.lineTo(x, y);
					c.stroke();
				}}
				onPointerUp={() => {
					drawing.current = false;
				}}
				onPointerCancel={() => {
					drawing.current = false;
				}}
			/>
			{hint !== undefined && <p className="text-[13px] text-fg-muted">{hint}</p>}
			<div className="grid grid-cols-2 gap-2">
				<Button size="lg" onClick={clear} disabled={!signed}>
					{clearLabel}
				</Button>
				<Button variant="primary" size="lg" onClick={done} disabled={!signed}>
					{doneLabel}
				</Button>
			</div>
		</div>
	);
}
